import React from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { Section } from '@/components/shared/layout/section';
import { removeSpan } from '@/components/shared/utility/format';
import { styled } from '@/stitches.config';
import { IIcon } from '@/types/shared/icons';

const BuyingToolHover = styled('div', {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  bc: '$gray300',
  clipPath: 'circle(0 at 50% 50%)',
  zIndex: '0',
  pe: 'none',
});

const BuyingToolItem = styled('div', {
  position: 'relative',
  flexGrow: '1',
  flexBasis: '0',
  maxWidth: '100%',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: '$gray300',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: '$gray300',
  '&:not(:last-child)': {
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: '$gray300',
  },
  '& .buying-tools-footer-link': {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    height: '100%',
    py: '$space-3',
    px: '$space-1',
    gap: '$space-2',
    overflow: 'hidden',
    '@xs': {
      px: '$space-3',
    },
    '@sm': {
      py: '$space-4',
    },
    '@md': {
      py: '$space-5',
      px: '$space-4',
    },
    '@lg': {
      py: '$space-8',
      px: '$space-7',
      alignItems: 'flex-start',
      textAlign: 'left',
    },
    '@xl': {
      py: '$space-9',
      px: '$8',
    },
    '@2xl': {
      py: '$space-10',
    },
    '@hover': {
      '&:hover': {
        [`& ${BuyingToolHover}`]: {
          '--transition-duration': '0.5s',
          clipPath: 'circle(70.71068% at 50% 50%)',
          transitionProperty: 'clip-path',
          transitionDelay: 'var(--transition-delay)',
          transitionDuration: 'var(--transition-duration)',
        },
      },
    },
  },
});

interface IProps {
  items?: {
    icon?: IIcon;
    label?: string;
    description?: string;
    url?: string;
    target?: null | '_blank';
  }[];
}

const View = ({ items = [] }: IProps) => {
  return (
    <Section
      as="div"
      backgroundColor="gray-light"
      data-test="section_buyingTools"
    >
      <Text as="h2" size="h5" font="bold" sr-only>
        สนใจรถ
      </Text>
      <Flex>
        {items.map((o, i) => (
          <BuyingToolItem key={i}>
            <Link
              className="buying-tools-footer-link"
              href={o.url}
              target={o.target}
              tracking={{
                dataTrack: 'footer',
                dataTrackText: removeSpan(o.label),
                dataTrackUrl: o.url,
              }}
            >
              <BuyingToolHover />
              {!!o.icon && (
                <Icon
                  icon={o?.icon}
                  size={{ '@initial': 'base', '@md': 'md', '@lg': 'lg' }}
                  color="icon-red"
                />
              )}
              {!!o.label && (
                <Text
                  size={{ '@initial': 'text-caption', '@md': 'text-body-2' }}
                  font="bold"
                  variant="text-gray-darkest"
                  wrap
                  dangerouslySetInnerHTML={{ __html: o.label }}
                  data-test="title"
                />
              )}
              {!!o.description && (
                <Text
                  size="text-body-2"
                  variant="text-gray-dark"
                  css={{ '@maxlg': { display: 'none' } }}
                  wrap
                  dangerouslySetInnerHTML={{ __html: o.description }}
                  data-test="description"
                />
              )}
            </Link>
          </BuyingToolItem>
        ))}
      </Flex>
    </Section>
  );
};

View.displayName = 'SectionSharedBuyingTool';
export default View;
